/**
 * Almacenamiento a Domicilio — Coverage map (single source of truth, frontend mirror).
 *
 * Must stay in sync with:
 *   spotme-server/src/constants/onDemandCoverage.ts
 *
 * Schema and resolution semantics are documented in the planning doc:
 *   docs/plans/2026-05-04-001-feat-almacenamiento-a-domicilio-plan.md
 *
 * Design notes:
 *  - This constant intentionally contains ZERO partner identifiers. Partner
 *    routing is handled manually by the SpotMe sales team via the HubSpot
 *    deal's `on_demand_city` property.
 *  - `partnerActive: false` cities are pre-wired but emit no surfaces — flip
 *    the flag in one PR to turn on every gated surface at once when a
 *    partner signs.
 *  - `metroAreaCities` accepts both `SearchPageCity` and `MexicoCityHoods`
 *    enum values because the SpotMe codebase classifies State of Mexico
 *    municipalities (Naucalpan, Tlalnepantla, etc.) as CDMX hoods, not
 *    standalone cities.
 */

import { MexicoCityHoods, SearchPageCity } from 'src/constants/searchPages'

export type OnDemandCity = 'monterrey' | 'cdmx' | 'guadalajara'

/**
 * A slug that can route to an on-demand market.
 *
 * Includes:
 *  - `OnDemandCity` so the canonical slug itself (e.g. `'cdmx'`) resolves —
 *    important because the URL segment uses the canonical slug, not the
 *    underlying `SearchPageCity` (`'ciudad-de-mexico'`).
 *  - `SearchPageCity` for cities that route to a market (e.g. `'apodaca'` → `'monterrey'`).
 *  - `MexicoCityHoods` for State of Mexico municipalities the codebase models
 *    as CDMX hoods (e.g. `'naucalpan'` → `'cdmx'`).
 */
export type OnDemandMetroAreaSlug =
  | OnDemandCity
  | SearchPageCity
  | MexicoCityHoods

/**
 * Geographic service area — center coordinates + radius in km.
 *
 * Drives the PRIMARY gating path for on-demand surfaces (search card, recovery
 * card). When a search result has lat/lng, we pick the on-demand market with a
 * service area containing the point — no slug matching required.
 *
 * Why coordinates instead of slugs: `metroAreaCities` is a hand-curated list
 * of `SearchPageCity` / `MexicoCityHoods` enum values. It misses real searches
 * like "Cumbres", "Carretera Nacional", "Tecnológico" because those are not
 * enumerated in our search-page constants. Coordinates handle every search
 * point in the metro automatically and generalize to new markets without
 * enumerating their hoods.
 *
 * Why a market carries a LIST of these: a partner's footprint is not always
 * one disc. Spakio serves the Valle de México and, from a separate Lerma
 * warehouse, the Toluca valley ~55 km west. A single circle wide enough to
 * reach Toluca also reaches Cuernavaca, which no partner serves — that exact
 * over-claim ran live 2026-08-17 → 08-19. Model each warehouse's catchment as
 * its own circle rather than inflating one radius to cover the farthest point.
 *
 * `metroAreaCities` is kept as a fallback for searches that arrive without
 * lat/lng (rare — the unified `/search` route always extracts coords from the
 * autocomplete Place; pillar pages also know their center via `searchPages`).
 */
export interface OnDemandServiceArea {
  /** Center latitude in decimal degrees. */
  centerLat: number
  /** Center longitude in decimal degrees. */
  centerLng: number
  /** Service radius in kilometers. Generous enough to cover the metro area
   *  including peripheral hoods/satellite cities a partner agreed to service. */
  radiusKm: number
}

export interface OnDemandCityConfig {
  /** Canonical slug for the on-demand market — matches the URL segment and HubSpot `on_demand_city` value. */
  canonicalSlug: OnDemandCity
  /** Conversational display name used in cards, hero subtitles, JSON-LD `name`,
   *  admin email subjects, and chatbot affirmations. Stays clean and short:
   *  "Monterrey", "Ciudad de México", "Guadalajara". MX colloquial usage
   *  already absorbs the metro into the city name (Monterrey → San Pedro,
   *  Apodaca; CDMX → Naucalpan, Tlalnepantla). The actual coverage signal
   *  for edge-case hoods comes from `coverageZones`, which names them. */
  displayName: string
  /** Acronym variant used in compact UI like city pickers and tile cards
   *  where horizontal space is tight ("CDMX" vs "Ciudad de México"). For
   *  Monterrey and Guadalajara this matches `displayName`. */
  displayShortName: string
  /** All canonical / `SearchPageCity` / `MexicoCityHoods` slug values that should route to this on-demand market. */
  metroAreaCities: OnDemandMetroAreaSlug[]
  /** Geographic service areas for coordinate-based gating — a point inside ANY
   *  of them resolves to this market. See {@link OnDemandServiceArea}. */
  serviceAreas: OnDemandServiceArea[]
  /** Gate for every downstream surface. `false` = no UI exposure, no sitemap entry, no JSON-LD, no chatbot referral. */
  partnerActive: boolean
  /** Hood/city names for the "atendemos en…" list and JSON-LD `areaServed`. Empty when `partnerActive` is false. */
  coverageZones: string[]
  /** Indicative monthly price per bundle-size tier (MXN/mes), anchored on the
   *  market partner's own published plans. Drives the landing page's JSON-LD
   *  `offers.lowPrice`/`highPrice` and the chatbot's on-demand preload, so it
   *  is a PUBLIC price claim — re-verify against the partner's live site when
   *  re-basing, never invent a ladder. */
  indicativePricing: {
    small: number
    medium: number
    large: number
  }
  /** Partner-derived per-m² monthly rates, duration-tiered. MXN/m²/mes.
   *  PER MARKET — each city is anchored on its OWN partner's published
   *  pricing (Monterrey → Boxmi, CDMX → Spakio); see the constants below for
   *  each derivation. Used by all search-card "from" price labels
   *  (OnDemandSearchCard / OnDemandRecoveryCard / pillar CrossLinkOnDemand)
   *  to anchor the cheapest tier as the conversion lure, while
   *  `indicativePricing` continues to drive bundle-size tiles on the
   *  landing page (HeroOnDemand + IndicativeSizesGrid). The duration-tier
   *  table on the landing page (`DurationTiers` in PartnerLearningSections)
   *  surfaces all three rates explicitly. */
  perSqmRates: {
    monthToMonth: number
    sixMonths: number
    twelveMonths: number
  }
  /** The market partner's OWN current promotion, quoted from its site and
   *  shown as «Promoción del operador» (never naming the partner). A PUBLIC
   *  claim: the monthly price-parity sweep re-reads `sourceUrl`; when the
   *  partner drops it, delete it here in BOTH repos (mirror). Omit = none. */
  partnerPromo?: {
    headline: string
    sourceUrl: string
    verifiedOn: string
  }
}

// Bundle-size entry prices (MXN/mes). PER MARKET — each set is anchored on the
// city's own fulfilment partner's published plans, so they are estimates of what
// the renter will actually be quoted, not a SpotMe-invented ladder.
// `large` is the entry price for its volume; sales quotes pro-rata above it
// (full apartments, mudanzas completas, business inventory). The
// `STARTING_FROM` copy already says "desde ${{price}} MXN/mes" which
// communicates the open-ended scaling.
//
// Monterrey — Boxmi sells per m² only (boxmi.mx/quoting), so these are its
// 12+ month rate ($399/m², re-read 2026-09-30) × the landing grid's estimated
// m² per tier (2 / 4 / 7, `SIZE_TO_M2`). The May capture (799 / 1,499 /
// 2,499) quoted medium and large BELOW Boxmi's own cheapest rate. `small`
// stays 799 (≥ 798) because the Meta catalog feed + pixel anchor on it.
const MTY_INDICATIVE_PRICING = {
  small: 799, // 2 m² × $399 (≈ 30 boxes)
  medium: 1596, // 4 m² × $399 (≈ a furnished bedroom)
  large: 2793 // 7 m² × $399 (≈ a full apartment, no upper cap)
} as const

// CDMX — Spakio's own published plans (spakio.com), captured 2026-08-28.
// Mapped straight onto their three consumer tiers rather than interpolated:
//   Chico    2.52 m³ / 1.8 m² → $599.25
//   Mediano  6.25 m³ / 4.5 m² → $1,399.50
//   Grande   12 m³   / 8.6 m² → $2,399.25
// Those are their CURRENT promotional prices (25% under the list prices their
// booking tool quotes: $799 / $1,866 / $3,199). If the promo lapses, these move
// up — re-check spakio.com before treating them as durable.
const CDMX_INDICATIVE_PRICING = {
  small: 599, // 2.52 m³ (≈ 30 boxes) — Spakio "Chico"
  medium: 1399, // 6.25 m³ (≈ a furnished bedroom) — Spakio "Mediano"
  large: 2399 // 12 m³+ (≈ a full apartment, no upper cap) — Spakio "Grande"
} as const

// Partner-derived per-m² duration-tier rates (MXN/m²/mes). PER MARKET, and
// each market keeps its OWN partner's ladder shape: every "−N%" the client
// shows (duration table, wizard options, price nudge) is computed from these
// numbers by `getTierDiscountPct`, never hardcoded in copy. Don't flatten the
// markets onto one ratio — that is how CDMX came to advertise a 12-month
// discount its partner doesn't offer.
//
// Monterrey — Boxmi's published per-m² card (boxmi.mx/quoting): $570 mes a
// mes / $456 6+ / $399 12+ (−20% / −30%). Captured 2026-05, re-read
// 2026-09-30 in the live quoting tool — unchanged.
const MTY_PER_SQM_RATES = {
  monthToMonth: 570,
  sixMonths: 456,
  twelveMonths: 399
} as const

// CDMX — derived from Spakio's published plans (spakio.com, captured 2026-08-28).
// Spakio does NOT sell per m²; it sells fixed m³ plans and states the m²
// equivalence itself ("Bodega de 20m3 … equivale aprox. a 14.3m2"), a constant
// 1.4 m effective stacking height. Dividing their prices by their own m² gives:
//   promo $333/m² (1.8 m²) → $311 (4.5) → $279 (8.6) → $259 (14.3) → $230 (50)
//   list  $444/m² (1.8 m²) → $415 (4.5) → $372 (8.6) → $317 (14.3) → $307 (50)
// Their per-m² falls with volume; ours is flat, so we anchor on the 2–7 m² band
// the wizard actually quotes (`volumeToEstimatedM2`).
// Spakio has ONE duration discount (spakio.com, re-read 2026-09-30): «Recomendamos
// un mínimo de 3 meses para que puedas obtener la recolección sin costo, además
// de un precio especial», with «no hay contrato de plazo forzoso». The special
// price is exactly 25% under list. So: 6+ and 12+ months are the SAME rate (the
// special-price band, ~$329/m²), and month-to-month is that ÷ 0.75 ($439, their
// list band) — the page then says −25%, the real gap. Until 2026-09-30 this was
// 470 / 376 / 329, a Monterrey-shaped ladder that advertised −20% at 6 months
// and −30% at 12 — a 12-month discount Spakio doesn't offer.
// Runs high at 7 m² (their per-m² keeps falling): over-quoting is the safe
// direction, since a renter who converts on an under-quote gets a bigger bill.
//
// ⚠️ These replaced a copy of the Monterrey/Boxmi card that was applied to CDMX
// in 2026-05 on the assumption "CDMX partner publishes identical rates" — written
// before Spakio was signed (2026-06-25) and never re-verified. At $399/m² we were
// quoting ABOVE Spakio's own list price for every plan ≥ 8.6 m². Do not re-flatten
// the two markets onto one constant.
const CDMX_PER_SQM_RATES = {
  monthToMonth: 439,
  sixMonths: 329,
  twelveMonths: 329
} as const

/**
 * Rates used for a market with no signed partner yet, and as the SSR-hydration
 * fallback in client surfaces that render a price before the city config
 * resolves. Monterrey's card, as the longest-running measured market.
 */
export const DEFAULT_PER_SQM_RATES = MTY_PER_SQM_RATES

export const ON_DEMAND_COVERAGE: Record<OnDemandCity, OnDemandCityConfig> = {
  monterrey: {
    canonicalSlug: 'monterrey',
    displayName: 'Monterrey',
    displayShortName: 'Monterrey',
    metroAreaCities: [
      SearchPageCity.Monterrey,
      SearchPageCity.SanPedro,
      SearchPageCity.SantaCatarina,
      SearchPageCity.Guadalupe,
      SearchPageCity.Apodaca,
      SearchPageCity.Escobedo,
      SearchPageCity.SanNicolas
    ],
    // Center near Macroplaza (Monterrey downtown). 35 km radius covers the
    // full metro: García/Escobedo (NW), Apodaca/Guadalupe (E),
    // San Pedro/Santa Catarina (SW), and the Sierra Madre foothills.
    serviceAreas: [{ centerLat: 25.67, centerLng: -100.31, radiusKm: 35 }],
    partnerActive: true,
    coverageZones: [
      'Monterrey',
      'San Pedro Garza García',
      'Santa Catarina',
      'Guadalupe',
      'Apodaca',
      'Escobedo',
      'San Nicolás',
      'García'
    ],
    indicativePricing: MTY_INDICATIVE_PRICING,
    perSqmRates: MTY_PER_SQM_RATES
  },
  cdmx: {
    canonicalSlug: 'cdmx',
    displayName: 'Ciudad de México',
    displayShortName: 'CDMX',
    // 'cdmx' (the canonical on-demand slug) is included explicitly so that
    // URL paths like `/renta-mini-bodegas/almacenamiento-a-domicilio/cdmx`
    // resolve. SearchPageCity.CiudadDeMexico ('ciudad-de-mexico') is the
    // pillar-page slug used elsewhere in the codebase.
    metroAreaCities: [
      'cdmx',
      SearchPageCity.CiudadDeMexico,
      SearchPageCity.Toluca,
      MexicoCityHoods.Naucalpan,
      MexicoCityHoods.Tlalnepantla,
      MexicoCityHoods.AtizapanDeZaragoza,
      MexicoCityHoods.CuautitlanIzcalli,
      MexicoCityHoods.Tultitlan
    ],
    // One circle per Spakio warehouse cluster:
    //   1. ZMVM, 50 km from the Zócalo — Naucalpan/Atizapán (NW),
    //      Cuautitlán/Tlalnepantla/Tultitlán/Tultepec (N), Ecatepec (E),
    //      Tlalpan/Xochimilco (S).
    //   2. Toluca valley, 30 km from Lerma — Toluca, Metepec, Zinacantepec and
    //      the Ocoyoacac warehouse itself ("Toluca y alrededores", the
    //      partner's own words). Stops ~50 km short of Cuernavaca.
    // Do NOT collapse these into one wider circle — see OnDemandServiceArea.
    serviceAreas: [
      { centerLat: 19.4326, centerLng: -99.1332, radiusKm: 50 },
      { centerLat: 19.2869, centerLng: -99.5103, radiusKm: 30 }
    ],
    // Activated 2026-08-17 — Spakio (warehouses in Tultitlán, Lerma and
    // Tultepec) covers CDMX + Área Metropolitana, plus Toluca y alrededores
    // via Lerma. Partner identity lives in DATA (Team.isPartner +
    // Team.onDemandMarkets via the provisionPartner script), never here.
    partnerActive: true,
    coverageZones: [
      'Polanco',
      'Condesa',
      'Roma',
      'Del Valle',
      'Coyoacán',
      'Tlalpan',
      'Naucalpan',
      'Tlalnepantla',
      'Atizapán',
      'Cuautitlán',
      'Tultitlán',
      'Toluca',
      'Lerma'
    ],
    indicativePricing: CDMX_INDICATIVE_PRICING,
    perSqmRates: CDMX_PER_SQM_RATES,
    // Spakio, spakio.com home (read 2026-09-30): «Recolección GRATIS al
    // almacenar 3 meses». Its «ahorra hasta 50%» is a comparison, not a promo.
    partnerPromo: {
      headline: 'Recolección gratis al almacenar 3 meses',
      sourceUrl: 'https://www.spakio.com',
      verifiedOn: '2026-09-30'
    }
  },
  guadalajara: {
    canonicalSlug: 'guadalajara',
    displayName: 'Guadalajara',
    displayShortName: 'Guadalajara',
    metroAreaCities: [
      SearchPageCity.Guadalajara,
      SearchPageCity.Zapopan,
      SearchPageCity.Tlaquepaque,
      SearchPageCity.Tonala,
      SearchPageCity.Tlajomulco
    ],
    // Center near Plaza de Armas (Guadalajara downtown). 30 km radius covers
    // Zapopan, Tlaquepaque, Tonalá and the Tlajomulco edge of the metro.
    serviceAreas: [{ centerLat: 20.6736, centerLng: -103.344, radiusKm: 30 }],
    // Pre-wired. Flip to `true` and populate `coverageZones` when a partner signs.
    partnerActive: false,
    coverageZones: [],
    indicativePricing: MTY_INDICATIVE_PRICING,
    perSqmRates: DEFAULT_PER_SQM_RATES
  }
}

export interface ResolvedOnDemandCity {
  city: OnDemandCity
  config: OnDemandCityConfig
  partnerActive: boolean
}

/**
 * The canonical on-demand METRO slug to put in a customer-facing on-demand URL
 * — or `null` when there is no safe value.
 *
 * ⚠️ **`partnerActive` is not advisory here, it is the 404 guard.** The landing
 * page `/renta-mini-bodegas/almacenamiento-a-domicilio/[city]` returns 404
 * unless the city resolves to a market with an ACTIVE partner (`guadalajara` is
 * enumerated but inactive today, so `/…/guadalajara` 404s right now). Anything
 * that composes that URL — the `ondemand_nurture_*` WhatsApp button suffix, the
 * `on_demand_market` contact stamp — must route through this helper and omit
 * the value entirely when it returns `null`, never substitute a guess.
 *
 * Returns the CANONICAL slug, so a metro-area member normalizes to its market
 * (`san-pedro-garza-garcia` → `monterrey`, `ciudad-de-mexico` → `cdmx`). That
 * matters because the page 301s a non-canonical slug, and a redirect inside a
 * WhatsApp button is a wasted hop.
 *
 * Added 2026-08-26: the nurture messages had the city HARDCODED to `monterrey`
 * from when MTY was the only on-demand market. CDMX went live with a partner on
 * 2026-08-17, so every CDMX renter was being sent to a Monterrey page. New
 * markets now flow through automatically the moment `partnerActive` flips.
 */
export function resolveOnDemandMarketSlug(
  slug: string | undefined | null
): string | null {
  const resolved = resolveOnDemandCity(slug)
  if (!resolved || !resolved.partnerActive) return null
  return resolved.config.canonicalSlug
}

/**
 * Resolves a city or hood slug to its on-demand market.
 *
 * Lowercase + trim input. Iterates `ON_DEMAND_COVERAGE`; the first key whose
 * `metroAreaCities` array includes the input wins. Returns `null` for empty,
 * undefined, null, or unknown input — never throws.
 *
 * The input is normalized via `slugifyCityInput` so display strings
 * ("Cuautitlán Izcalli", "ATIZAPÁN DE ZARAGOZA") resolve as if they were the
 * canonical slug. This is defense-in-depth: the analysis prompt is supposed
 * to emit canonical slugs, but a near-miss should not silently drop leads.
 *
 * Callers gate every on-demand surface on the returned `partnerActive` flag.
 *
 * Prefer {@link resolveOnDemandCityByCoords} when the search has lat/lng —
 * coords cover every point in the metro automatically, while this helper is
 * limited to the slugs we've enumerated in `metroAreaCities`.
 */
/**
 * Resolves a city or hood slug to its on-demand market.
 *
 * Lowercase + trim input. Iterates `ON_DEMAND_COVERAGE`; the first key whose
 * `metroAreaCities` array includes the input wins. Returns `null` for empty,
 * undefined, null, or unknown input — never throws.
 *
 * Callers gate every on-demand surface on the returned `partnerActive` flag.
 *
 * Prefer {@link resolveOnDemandCityByCoords} when the search has lat/lng —
 * coords cover every point in the metro automatically, while this helper is
 * limited to the slugs we've enumerated in `metroAreaCities`.
 */
export function resolveOnDemandCity(
  slug: string | undefined | null
): ResolvedOnDemandCity | null {
  if (slug === undefined || slug === null) return null
  const normalized = slug.trim().toLowerCase()
  if (normalized === '') return null

  const entries = Object.entries(ON_DEMAND_COVERAGE) as [
    OnDemandCity,
    OnDemandCityConfig
  ][]

  for (const [city, config] of entries) {
    if (config.metroAreaCities.includes(normalized as OnDemandMetroAreaSlug)) {
      return { city, config, partnerActive: config.partnerActive }
    }
  }

  return null
}

/**
 * Metro slugs that name a municipality which ALSO exists in other Mexican
 * states, so the bare slug does not identify a place on its own.
 *
 * `metroAreaCities` is a list of OUR canonical `SearchPageCity` values, and in
 * that namespace `'guadalupe'` unambiguously means Guadalupe, Nuevo León. The
 * ambiguity only appears when a slug arrives from FREE TEXT — a renter typing
 * "guadalupe. zac" is talking about Zacatecas, ~700 km outside every market we
 * serve.
 *
 * Audited against all three markets' `metroAreaCities` (2026-08-11). The
 * qualified slugs are deliberately absent: `san-pedro-garza-garcia`,
 * `general-escobedo`, `san-pedro-tlaquepaque` and `tlajomulco-de-zuniga` all
 * carry their disambiguator already, and `monterrey`/`guadalajara`/`zapopan`/
 * `apodaca` have no meaningful national twin. Re-audited 2026-08-17 for the
 * CDMX activation: `toluca` and `tultitlan` are unique to Estado de México —
 * no addition needed.
 *
 * Keep this list in step with `metroAreaCities` — adding a bare municipality
 * name to a market means checking it here in the same change.
 *
 * MIRRORED from `spotme-server/src/constants/onDemandCoverage.ts` — edit both.
 */
export const AMBIGUOUS_METRO_AREA_SLUGS: readonly string[] = [
  'guadalupe', // Guadalupe NL · Zacatecas · Chihuahua
  'santa-catarina', // Santa Catarina NL · Guanajuato · SLP · many in Oaxaca
  'san-nicolas', // San Nicolás de los Garza NL · Tolentino SLP · de los Ranchos Puebla
  'tonala' // Tonalá Jalisco · Tonalá Chiapas
]

/**
 * Resolves a city slug that came from UNTRUSTED free text to its on-demand
 * market.
 *
 * Identical to {@link resolveOnDemandCity} except that a state-ambiguous slug
 * resolves to `null` instead of silently picking the market that happens to
 * contain a same-named municipality.
 *
 * Callers holding a canonical `SearchPageCity` from our OWN routing (search
 * pages, SEO footer, pillar/city pages, sitemap, JSON-LD) must keep using
 * {@link resolveOnDemandCity} — for them `'guadalupe'` really is Guadalupe NL,
 * and refusing it would silently un-launch a live market surface.
 *
 * Exists on the client only to keep this mirrored constant identical to the
 * server's; the chatbot path that needed it is backend-side.
 */
export function resolveOnDemandCityFromUserInput(
  slug: string | undefined | null
): ResolvedOnDemandCity | null {
  if (slug === undefined || slug === null) return null
  const normalized = slug.trim().toLowerCase()
  if (AMBIGUOUS_METRO_AREA_SLUGS.includes(normalized)) return null
  return resolveOnDemandCity(normalized)
}

const EARTH_RADIUS_KM = 6371

/**
 * Great-circle distance in kilometers between two lat/lng points (haversine).
 *
 * Pure helper — no external deps. Inputs in decimal degrees. Order of
 * magnitude is enough for our gating decisions (radius checks at 30-50 km),
 * so we use a fixed Earth radius rather than the ellipsoidal model.
 */
export function haversineKm(
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number
): number {
  const toRad = (deg: number) => (deg * Math.PI) / 180
  const dLat = toRad(lat2 - lat1)
  const dLng = toRad(lng2 - lng1)
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
  return EARTH_RADIUS_KM * c
}

/**
 * Resolves a search point (lat/lng) to the on-demand market that covers it.
 *
 * PRIMARY gating path. Scans every market's `serviceAreas` and picks the
 * circle that contains the point with the smallest center-to-point distance —
 * handles overlapping circles (both across markets and within one market's
 * own list) by preferring the closer one. Returns `null` when no service area
 * contains the point.
 *
 * Generalizes to any new market or new warehouse: drop in another
 * `{ centerLat, centerLng, radiusKm }` and every search point inside it
 * surfaces the on-demand card. No need to enumerate hoods.
 *
 * Callers should still check `partnerActive` on the returned config — this
 * helper returns inactive markets too, so an inactive market in radius can
 * be surfaced as a "coming soon" hint if we ever want that. Today, every
 * caller filters on `partnerActive`.
 */
export function resolveOnDemandCityByCoords(
  lat: number | undefined | null,
  lng: number | undefined | null
): ResolvedOnDemandCity | null {
  if (
    lat === undefined ||
    lat === null ||
    lng === undefined ||
    lng === null ||
    Number.isNaN(lat) ||
    Number.isNaN(lng)
  ) {
    return null
  }

  let best: {
    city: OnDemandCity
    config: OnDemandCityConfig
    km: number
  } | null = null

  const entries = Object.entries(ON_DEMAND_COVERAGE) as [
    OnDemandCity,
    OnDemandCityConfig
  ][]

  for (const [city, config] of entries) {
    for (const area of config.serviceAreas) {
      const km = haversineKm(lat, lng, area.centerLat, area.centerLng)
      if (km <= area.radiusKm && (!best || km < best.km)) {
        best = { city, config, km }
      }
    }
  }

  if (!best) return null
  return {
    city: best.city,
    config: best.config,
    partnerActive: best.config.partnerActive
  }
}
